import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Target, RefreshCw, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

export const TossRunButton = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isRunning, setIsRunning] = useState(false);

  const handleRunToss = async () => {
    setIsRunning(true);
    
    toast({
      title: 'Starting TOSS Analysis',
      description: 'Step 1/2: Collecting DeFi protocol revenue & TVL data...',
    });

    try {
      // Step 1: Collect DefiLlama data (TVL & revenue)
      const { data: defiData, error: defiError } = await supabase.functions.invoke('collect-defillama-data');
      
      if (defiError) throw new Error(`Data collection failed: ${defiError.message}`);

      toast({
        title: 'Data Collection Complete',
        description: `Step 2/2: Running TOSS scoring on ${defiData?.fundamentals_collected || 0} protocols...`,
      });

      // Step 2: Run TOSS analysis with enhanced data
      const { data, error } = await supabase.functions.invoke('enhanced-market-analysis');

      if (error) throw error;

      const metadata = data?.metadata;
      const storageSuccess = metadata?.opportunities_stored > 0;

      toast({
        title: storageSuccess ? '🔥 TOSS Analysis Complete!' : 'Analysis Complete (Warning)',
        description: storageSuccess 
          ? `Found ${metadata.opportunities_stored} investment opportunities with revenue data! Scroll down to see results.`
          : 'Analysis complete but no opportunities were found in the target range. Try adjusting filters.',
        variant: storageSuccess ? 'default' : 'destructive',
      });
      
      // Invalidate cache so TossWidget refetches immediately
      queryClient.invalidateQueries({ queryKey: ['top-opportunities'] });

      if (storageSuccess) {
        // Scroll to TOSS widget
        setTimeout(() => {
          const tossSection = document.getElementById('toss-widget');
          if (tossSection) {
            tossSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    } catch (error) {
      console.error('TOSS analysis error:', error);
      toast({
        title: 'Analysis Failed',
        description: error instanceof Error ? error.message : 'Failed to run TOSS analysis',
        variant: 'destructive',
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-pink-500 rounded-lg blur opacity-30 animate-pulse"></div>
      <Button
        onClick={handleRunToss}
        disabled={isRunning}
        size="lg"
        className="relative bg-gradient-to-r from-primary via-purple-500 to-pink-500 hover:from-primary/90 hover:via-purple-500/90 hover:to-pink-500/90 text-white font-bold shadow-lg hover:shadow-xl transition-all"
      >
        {isRunning ? (
          <>
            <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
            Running TOSS Analysis...
          </>
        ) : (
          <>
            <Zap className="mr-2 h-5 w-5" />
            <Target className="mr-2 h-5 w-5" />
            Run TOSS Analysis
          </>
        )}
      </Button>
    </div>
  );
};
